import { MobileDrawer } from '@components/MobileDrawer/MobileDrawer';
import { SortButton } from '@components/SortSelect/SortButton';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { folderPathDepth } from '@lib/library/folderPath';
import { FOLDER_SORT_OPTIONS, folderSortLabel, isFolderSortKey } from '@lib/library/folderSort';
import { frostedGlassTransitionProps } from '@lib/utils/frostedPopoverTransition';
import { Popover } from '@mui/material';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import css from './AllFoldersButton.module.scss';
import { folderUrl } from './FolderDialogs';
import {
  folderLocation,
  FolderSearchField,
  folderTreeCss,
  FolderTreeLabel,
  FolderTreeRow,
  searchFolders,
  useFolderTreeExpansion,
} from './FolderTreeView';

/**
 * Every folder of the library, searchable by name — the way to a folder once there are more than
 * the row under the header can show.
 *
 * With no query it is the tree (`FolderTree`), opening around the folder you're in. With one it is
 * the matching folders from anywhere in the library, each with the path that leads to it. The
 * library's own row is left out: the page is already that library, and the header's title and
 * breadcrumb are the way back to its root.
 */
const FolderFinder = observer(function FolderFinder({ onDone }: { onDone: () => void }) {
  const folder = useLibraryFolder();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const tree = useFolderTreeExpansion(folder, folder.currentFolderPath);

  const needle = query.trim().toLowerCase();
  // The library's own row is left out: the page is that library. Search results are one flat
  // list, so they are sorted as one; the tree orders each folder's subfolders among themselves.
  const rows = needle
    ? searchFolders(folder, needle).sort((a, b) => folder.compareFolders(a.entry, b.entry))
    : searchFolders(folder, '').filter((row) => tree.isVisible(row.path));

  const openFirst = () => {
    if (rows.length === 0) return;
    navigate(folderUrl(folder, rows[0].path));
    onDone();
  };

  return (
    <div className={css.finder}>
      <div className={css.header}>
        <span className={css.title}>Folders</span>
        <span className={css.total}>{folder.folders.length}</span>
        <div className={css.sort}>
          <SortButton
            variant="text"
            tooltip="Sort folders by"
            value={folder.folderSort}
            selectedLabel={folderSortLabel(folder.folderSort)}
            options={FOLDER_SORT_OPTIONS.map((group) => [...group])}
            onChange={(next) => isFolderSortKey(next) && folder.setFolderSort(next)}
          />
        </div>
      </div>
      <FolderSearchField value={query} onChange={setQuery} onSubmitFirst={openFirst} />
      <div className={css.list} role="tree">
        {rows.map(({ path, entry }) => {
          const count = folder.folderSummary(path).mediaCount;
          return (
            <FolderTreeRow
              key={path}
              depth={needle ? 0 : folderPathDepth(path)}
              color={entry.color}
              expandable={!needle && tree.hasChildren(path)}
              expanded={tree.isExpanded(path)}
              onToggle={() => tree.toggle(path)}
              name={entry.name}
              className={classNames(path === folder.currentFolderPath && folderTreeCss.current)}
            >
              <Link to={folderUrl(folder, path)} className={folderTreeCss.rowMain} onClick={onDone}>
                <FolderTreeLabel
                  entry={entry}
                  name={entry.name}
                  needle={needle}
                  subtitle={needle && folderLocation(folder, path)}
                  trailing={count > 0 && <span className={folderTreeCss.rowCount}>{count}</span>}
                />
              </Link>
            </FolderTreeRow>
          );
        })}
        {rows.length === 0 && (
          <span className={css.empty}>
            {needle ? `No folder named “${query.trim()}”` : 'No folders yet'}
          </span>
        )}
      </div>
    </div>
  );
});

/** The row's way out when it overflows: "All folders", opening `FolderFinder`. */
export const AllFoldersButton = observer(function AllFoldersButton() {
  const isMobile = useIsMobile();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const close = () => {
    setAnchorEl(null);
    setDrawerOpen(false);
  };

  return (
    <>
      <button
        type="button"
        className={css.button}
        aria-haspopup="dialog"
        onClick={(event) => (isMobile ? setDrawerOpen(true) : setAnchorEl(event.currentTarget))}
      >
        {/* No count: the row's own number disagreed with the whole-library tree this opens, and
            the library's number was bigger than any row the user could see. */}
        All folders
      </button>
      {isMobile ? (
        <MobileDrawer open={drawerOpen} onClose={close} onOpen={() => setDrawerOpen(true)}>
          <FolderFinder onDone={close} />
        </MobileDrawer>
      ) : (
        <Popover
          open={anchorEl !== null}
          anchorEl={anchorEl}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            transition: frostedGlassTransitionProps,
            paper: {
              sx: {
                background: 'rgba(30, 30, 30, 0.85)',
                backdropFilter: 'blur(50px)',
                WebkitBackdropFilter: 'blur(50px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                boxShadow:
                  '0 24px 32px -12px rgba(18, 18, 18, 0.6), 0 8px 24px -8px rgba(0, 0, 0, 0.4)',
                marginTop: '8px',
                overflow: 'hidden',
              },
            },
          }}
        >
          <FolderFinder onDone={close} />
        </Popover>
      )}
    </>
  );
});
